import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Wrench, Car, TrendingUp, DollarSign, Calendar, 
  BarChart3, RefreshCw, Filter, X, FileText
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';
import { useTranslation } from "../contexts/I18nContext";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

export default function ReportManutenzioniPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [veicoli, setVeicoli] = useState([]);
  const [filtri, setFiltri] = useState({
    anno: new Date().getFullYear(),
    automezzo_id: ""
  });

  const fetchVeicoli = useCallback(async () => {
    const prefix = getApiPrefix();
    try {
      const res = await api.get(`${prefix}/automezzi`);
      setVeicoli(res.data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('Failed to fetch veicoli:', err);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    const prefix = getApiPrefix();
    try {
      const params = new URLSearchParams();
      if (filtri.anno) params.append('anno', filtri.anno);
      if (filtri.automezzo_id) params.append('automezzo_id', filtri.automezzo_id);
      
      const res = await api.get(`${prefix}/reports/manutenzioni?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      
      handleApiError(err, t("errors.errorLoadReport"));
    } finally {
      setLoading(false);
    }
  }, [filtri, t]);

  useEffect(() => {
    fetchVeicoli();
  }, [fetchVeicoli]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const resetFiltri = () => {
    setFiltri({
      anno: new Date().getFullYear(),
      automezzo_id: ""
    });
  };

  const anni = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => current - i);
  }, []);

  const trendData = useMemo(() => {
    if (!report?.trend_mensile) return [];
    return [...report.trend_mensile].reverse().map(item => ({
      ...item,
      label: item.periodo
    }));
  }, [report]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const riepilogo = report?.riepilogo || {};
  const perVeicolo = report?.per_veicolo || [];
  const perTipo = report?.per_tipo_intervento || [];
  const dettaglio = report?.dettaglio_manutenzioni || [];

  return (
    <div className="space-y-6" data-testid="report-manutenzioni-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">{t("nav.maintenanceReport")}</h1>
            <p className="text-muted-foreground">Analisi costi manutenzione veicoli</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchReport} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{t('common.refresh')}</Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />{t('common.filters')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetFiltri}>
              <X className="h-4 w-4 mr-1" />{t('common.reset')}</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{t("common.year")}</Label>
              <Select 
                value={filtri.anno?.toString() || "all"} 
                onValueChange={(v) => setFiltri({ ...filtri, anno: v === "all" ? null : parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tutti gli anni" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli anni</SelectItem>
                  {anni.map(anno => (
                    <SelectItem key={anno} value={anno.toString()}>{anno}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('leasing.vehicle')}</Label>
              <SearchableSelect
                options={[
                  { value: "all", label: "Tutti i veicoli" },
                  ...veicoli.map(v => ({ value: v.id, label: `${v.marca} ${v.modello} (${v.targa})` }))
                ]}
                value={filtri.automezzo_id || "all"}
                onValueChange={(v) => setFiltri({ ...filtri, automezzo_id: v === "all" ? "" : v })}
                placeholder="Tutti i veicoli"
                searchPlaceholder="Cerca veicolo..."
                testId="filter-veicolo"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <DollarSign className="h-10 w-10 text-blue-600" />
              <div>
                <p className="text-xs text-blue-700 uppercase font-medium">{t('common.totalCosts')}</p>
                <p className="text-2xl font-bold text-blue-800">
                  {formatCurrency(riepilogo.totale_costi || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Wrench className="h-10 w-10 text-green-600" />
              <div>
                <p className="text-xs text-green-700 uppercase font-medium">N° Manutenzioni</p>
                <p className="text-2xl font-bold text-green-800">
                  {riepilogo.totale_manutenzioni || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Car className="h-10 w-10 text-orange-600" />
              <div>
                <p className="text-xs text-orange-700 uppercase font-medium">Veicoli Coinvolti</p>
                <p className="text-2xl font-bold text-orange-800">
                  {riepilogo.num_veicoli || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-10 w-10 text-purple-600" />
              <div>
                <p className="text-xs text-purple-700 uppercase font-medium">Costo Medio</p>
                <p className="text-2xl font-bold text-purple-800">
                  {formatCurrency(riepilogo.costo_medio_per_manutenzione || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Costi per Veicolo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-500" />
              Costi per Veicolo
            </CardTitle>
          </CardHeader>
          <CardContent>
            {perVeicolo.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={perVeicolo.filter(v => v.totale_costi > 0).slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="targa" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `€${v/1000}k`} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    labelFormatter={(label) => {
                      const v = perVeicolo.find(x => x.targa === label);
                      return v ? `${v.marca} ${v.modello} (${v.targa})` : label;
                    }}
                  />
                  <Bar dataKey="totale_costi" name="Costi" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">{t('common.noData')}</div>
            )}
          </CardContent>
        </Card>

        {/* Costi per Tipo Intervento */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="h-5 w-5 text-green-500" />
              Costi per Tipo Intervento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {perTipo.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={perTipo}
                    dataKey="totale_costi"
                    nameKey="tipo_intervento"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ tipo_intervento, percent }) => `${tipo_intervento} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={{ stroke: '#666', strokeWidth: 1 }}
                  >
                    {perTipo.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">{t('common.noData')}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend Mensile */}
      {trendData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-purple-500" />
              Trend Costi Mensili
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v/1000}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="totale_costi" 
                  name="Costi" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Tabelle Dettaglio */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Dettaglio per Veicolo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Dettaglio per Veicolo</CardTitle>
            <CardDescription>Riepilogo costi per ogni veicolo</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.vehicle')}</TableHead>
                  <TableHead className="text-center">N° Manutenzioni</TableHead>
                  <TableHead className="text-right">{t('common.totalCosts')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perVeicolo.filter(v => v.num_manutenzioni > 0).map((v) => (
                  <TableRow key={v.automezzo_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{v.marca} {v.modello}</p>
                        <p className="text-xs text-muted-foreground">{v.targa}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{v.num_manutenzioni}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(v.totale_costi)}
                    </TableCell>
                  </TableRow>
                ))}
                {perVeicolo.filter(v => v.num_manutenzioni > 0).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      Nessuna manutenzione nel periodo selezionato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dettaglio per Tipo Intervento */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Dettaglio per Tipo Intervento</CardTitle>
            <CardDescription>Riepilogo costi per tipologia</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo Intervento</TableHead>
                  <TableHead className="text-center">N° Interventi</TableHead>
                  <TableHead className="text-right">{t('common.totalCosts')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {perTipo.map((t, idx) => (
                  <TableRow key={t.tipo_intervento_id || idx}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        />
                        <span className="font-medium">{t.tipo_intervento}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{t.num_interventi}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(t.totale_costi)}
                    </TableCell>
                  </TableRow>
                ))}
                {perTipo.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      Nessun intervento nel periodo selezionato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Ultime Manutenzioni */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-500" />
            Ultime Manutenzioni
          </CardTitle>
          <CardDescription>Dettaglio delle ultime 50 manutenzioni</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.date")}</TableHead>
                <TableHead>{t('common.vehicle')}</TableHead>
                <TableHead>{t('dialogs.workshop')}</TableHead>
                <TableHead className="text-center">KM</TableHead>
                <TableHead className="text-right">{t("common.total")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dettaglio.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      {new Date(m.data).toLocaleDateString('it-IT')}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{m.veicolo}</TableCell>
                  <TableCell>{m.officina || '-'}</TableCell>
                  <TableCell className="text-center">
                    {m.km_attuali ? m.km_attuali.toLocaleString('it-IT') : '-'}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(m.totale)}
                  </TableCell>
                </TableRow>
              ))}
              {dettaglio.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    Nessuna manutenzione nel periodo selezionato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
